// packages/pulse-sdk/src/components.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Headless-ish presentational components. Each renders with stable `pulse-*`
// classNames AND minimal inline styles, so a host with NO CSS still gets a
// reasonable render, while a host with its own design system can override every
// element by targeting the classNames. None of these own data fetching beyond
// the SDK hooks; none own auth.
// ─────────────────────────────────────────────────────────────────────────────

import type { CSSProperties, ReactNode } from "react";
import {
  useArc,
  useBodyMetrics,
  useChatStatus,
  useGuardianGate,
} from "./hooks";
import { PulseApiError } from "./client";
import type { ArcTile, BodyMetric } from "./types";

// ── shared bits ────────────────────────────────────────────────────────────────
function Loading({ label }: { label: string }) {
  return (
    <div className="pulse-loading" style={{ padding: 16, opacity: 0.7, fontSize: 14 }}>
      {label}
    </div>
  );
}

function ErrorBox({ error }: { error: unknown }) {
  const msg =
    error instanceof PulseApiError
      ? `${error.status}: ${error.message}`
      : error instanceof Error
        ? error.message
        : "something went wrong";
  return (
    <div
      className="pulse-error"
      role="alert"
      style={{
        padding: 16,
        fontSize: 14,
        color: "#b91c1c",
        border: "1px solid #fecaca",
        borderRadius: 8,
        background: "#fef2f2",
      }}
    >
      {msg}
    </div>
  );
}

function Empty({ title, hint }: { title: string; hint?: string }) {
  return (
    <div
      className="pulse-empty"
      style={{
        padding: 24,
        textAlign: "center",
        border: "1px dashed #d4d4d8",
        borderRadius: 8,
        color: "#71717a",
      }}
    >
      <p style={{ fontWeight: 600, margin: 0 }}>{title}</p>
      {hint ? <p style={{ fontSize: 13, marginTop: 6 }}>{hint}</p> : null}
    </div>
  );
}

const cardStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  padding: 16,
  border: "1px solid #e4e4e7",
  borderRadius: 12,
  background: "#fff",
  minWidth: 0,
};

// ── <PulseRecommendations> — the marquee ARC tile band ──────────────────────────
export interface PulseRecommendationsProps {
  participantId?: number;
  /** Max tiles to request/render (server clamps to [1, 20]). Default 5. */
  max?: number;
  /**
   * Called when a tile's primary action is activated. Receives the tile and its
   * deep link. If omitted, the action renders as an <a href={deepLink}>.
   */
  onTileAction?: (tile: ArcTile) => void;
  className?: string;
}

export function PulseRecommendations({
  participantId,
  max = 5,
  onTileAction,
  className,
}: PulseRecommendationsProps) {
  const { data: tiles, isLoading, error } = useArc(participantId, { max });

  if (isLoading) return <Loading label="Loading recommendations…" />;
  if (error) return <ErrorBox error={error} />;
  if (!tiles || tiles.length === 0) {
    return (
      <Empty
        title="No active recommendations yet"
        hint="Complete an intake conversation to generate your first recommendation context."
      />
    );
  }

  return (
    <div
      className={`pulse-recommendations${className ? ` ${className}` : ""}`}
      aria-label="Active Recommendation Context"
      style={{
        display: "grid",
        gap: 16,
        gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
      }}
    >
      {tiles.map((tile) => (
        <ArcTileCard key={tile.id} tile={tile} onTileAction={onTileAction} />
      ))}
    </div>
  );
}

function ArcTileCard({
  tile,
  onTileAction,
}: {
  tile: ArcTile;
  onTileAction?: (tile: ArcTile) => void;
}) {
  const actionLabel = tile.primaryAction.label;
  return (
    <div className="pulse-tile" style={cardStyle}>
      <span
        className="pulse-tile-specialist"
        style={{
          width: "fit-content",
          borderRadius: 999,
          padding: "2px 8px",
          fontSize: 11,
          fontWeight: 500,
          background: "rgba(79,70,229,0.12)",
          color: "#4f46e5",
        }}
      >
        {tile.specialist}
      </span>
      <h3 className="pulse-tile-title" style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
        {tile.summary}
      </h3>
      {tile.detail ? (
        <p
          className="pulse-tile-detail"
          style={{ flex: 1, margin: 0, fontSize: 13, color: "#52525b" }}
        >
          {tile.detail}
        </p>
      ) : null}
      <div
        className="pulse-tile-footer"
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 4 }}
      >
        {onTileAction ? (
          <button
            type="button"
            className="pulse-tile-action"
            onClick={() => onTileAction(tile)}
            style={{
              border: "none",
              background: "none",
              padding: 0,
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 500,
              color: "#4f46e5",
            }}
          >
            {actionLabel} →
          </button>
        ) : (
          <a
            className="pulse-tile-action"
            href={tile.primaryAction.deepLink}
            style={{ fontSize: 13, fontWeight: 500, color: "#4f46e5" }}
          >
            {actionLabel} →
          </a>
        )}
        {tile.citations[0] ? (
          <a
            className="pulse-tile-citation"
            href={tile.citations[0].url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 12, color: "#71717a" }}
          >
            {tile.citations[0].journal}
          </a>
        ) : null}
      </div>
    </div>
  );
}

// ── <BodyMetrics> — recent body composition rows ────────────────────────────────
export interface BodyMetricsProps {
  participantId?: number;
  /** Show at most this many recent rows. Default 10. */
  limit?: number;
  className?: string;
}

export function BodyMetrics({ participantId, limit = 10, className }: BodyMetricsProps) {
  const { data, isLoading, error } = useBodyMetrics(participantId);

  if (isLoading) return <Loading label="Loading body metrics…" />;
  if (error) return <ErrorBox error={error} />;
  if (!data || data.length === 0) {
    return <Empty title="No body metrics yet" hint="Log a weigh-in to see trends here." />;
  }

  const rows = data.slice(0, limit);
  return (
    <div className={`pulse-body-metrics${className ? ` ${className}` : ""}`} style={cardStyle}>
      <table className="pulse-body-metrics-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ textAlign: "left", color: "#71717a" }}>
            <th style={thStyle}>Date</th>
            <th style={thStyle}>Weight (lb)</th>
            <th style={thStyle}>Body fat %</th>
            <th style={thStyle}>Source</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((m: BodyMetric) => (
            <tr key={m.id} style={{ borderTop: "1px solid #f4f4f5" }}>
              <td style={tdStyle}>{m.date}</td>
              <td style={tdStyle}>{m.weightLbs ?? "—"}</td>
              <td style={tdStyle}>{m.bodyFatPct ?? "—"}</td>
              <td style={tdStyle}>{m.source}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const thStyle: CSSProperties = { padding: "4px 8px", fontWeight: 500 };
const tdStyle: CSSProperties = { padding: "6px 8px" };

// ── <PulseThread> — intake conversation status (read-only until Phase 5) ────────
export interface PulseThreadProps {
  participantId?: number;
  className?: string;
}

export function PulseThread({ participantId, className }: PulseThreadProps) {
  // participantId is reserved for the Phase-5 thread-read endpoint; the current
  // status surface is always the authed user's.
  void participantId;
  const { data, isLoading, error } = useChatStatus();

  if (isLoading) return <Loading label="Loading conversation…" />;
  if (error) return <ErrorBox error={error} />;
  if (!data) return <Empty title="No conversation yet" />;

  return (
    <div className={`pulse-thread${className ? ` ${className}` : ""}`} style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span className="pulse-thread-state" style={{ fontWeight: 600 }}>
          {data.isComplete ? "Intake complete" : "Intake in progress"}
        </span>
        <span style={{ fontSize: 12, color: "#71717a" }}>
          {data.totalTurns ?? 0} turns
        </span>
      </div>
      {data.turnsBySpecialist && data.turnsBySpecialist.length > 0 ? (
        <ul className="pulse-thread-specialists" style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: "#52525b" }}>
          {data.turnsBySpecialist.map((t) => (
            <li key={t.specialist}>
              {t.specialist}: {t.turns}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

// ── <TrustCard> — placeholder (trust write surface ships in Phase 5) ────────────
export interface TrustCardProps {
  participantId?: number;
  className?: string;
}

export function TrustCard({ participantId, className }: TrustCardProps) {
  void participantId;
  return (
    <div className={`pulse-trust-card${className ? ` ${className}` : ""}`} style={cardStyle}>
      <span style={{ fontWeight: 600 }}>Trust &amp; verification</span>
      <p style={{ margin: 0, fontSize: 13, color: "#71717a" }}>
        Credential and identity verification details will appear here once the trust
        surface ships (Phase 5).
      </p>
    </div>
  );
}

// ── <GuardianGate> — render children only if the minor-data gate passes ─────────
// The gate is evaluated server-side (GET /api/pulse/guardian-gate), which records
// a minor_access_audit row on every successful check for minors. A failed gate
// (minor with no active guardian link) surfaces as a 403 and renders `fallback`.
export interface GuardianGateProps {
  participantId?: number;
  children: ReactNode;
  /** Rendered when the gate fails or errors. Defaults to a short notice. */
  fallback?: ReactNode;
  /** Rendered while the gate is being evaluated. Defaults to a spinner. */
  loading?: ReactNode;
}

export function GuardianGate({
  participantId,
  children,
  fallback,
  loading,
}: GuardianGateProps) {
  const { data, isLoading, error } = useGuardianGate(participantId);

  if (isLoading) return <>{loading ?? <Loading label="Checking access…" />}</>;
  if (error || !data?.allowed) {
    return (
      <>
        {fallback ?? (
          <Empty
            title="Access restricted"
            hint="This participant's data requires an active guardian link."
          />
        )}
      </>
    );
  }
  return <>{children}</>;
}
